import React, { useState, useCallback, Suspense, lazy, Profiler } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import NavBar from './components/shared/common/NavBar';
import './utils/env';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { SectionErrorFallback } from './components/shared/ui/SectionErrorFallback';
import { UserProvider, useUser } from './contexts/UserContext';
import { SaveStatusProvider } from './contexts/SaveStatusContext';
import { WeekProvider, useWeekCalculation } from './hooks/useWeekSelection';
import { SnapshotScopeProvider } from './contexts/SnapshotScope';
import { NotesFlushProvider, useNotesFlushOptional } from './contexts/NotesFlushContext';
import { SnapshotFlushProvider, useSnapshotFlush } from './contexts/SnapshotFlushContext';
import { useTabNavigationShortcuts } from './hooks/useKeyboardShortcuts';
import 'tippy.js/dist/tippy.css';
import type { CampaignTransaction } from './types';
import AuthGuard from './components/auth/AuthGuard';
import FloatingSessionControl from './components/shared/ui/FloatingSessionControl';
import { AuthDiagnostics } from './components/shared/ui/AuthDiagnostics';
import { initRUM } from './utils/rum';
import { logger } from './utils/logger';
import { performanceDashboard, initializePerformanceOptimizations } from './utils/performance';
import { applyMobileOptimizations } from './utils/mobileDetection';
import ToastContainer from './components/shared/ui/ToastContainer';
import DebugSnapshotsPanel from './components/dev/DebugSnapshotsPanel';
import { useSectionExposureAnalytics } from './hooks/useSectionExposureAnalytics';
import { trackProductEvent } from './utils/productAnalytics';
// Conditionally import SmokeTest only in dev/test mode
// Note: Errors are logged as debug only - fallback handles gracefully
const SmokeTest = import.meta.env.VITE_SMOKE_TEST === 'true' 
  ? React.lazy(() => 
      import('./components/SmokeTest').catch((error) => {
        // Only log in dev mode - fallback component handles this gracefully
        if (import.meta.env.DEV) {
          logger.debug('SmokeTest lazy load failed, using fallback', { error }, 'App');
        }
        return { default: () => null };
      })
    )
  : null;

// Direct imports for instant loading (no code-splitting delay)
import CampaignSpend from './sections/Spend/CampaignSpend';
import SpendFilterSidebar from './components/shared/ui/SpendFilterSidebar';
import NotesSection from './sections/Notes/NotesSection';
import PromoCodesSection from './sections/Codes/PromoCodesSection';
import UpdatesSection from './sections/Updates/UpdatesSection';

// Keep lazy loading for heavy components (below fold)
// Add error handling to catch module loading failures
// Note: Errors are logged as debug/warn only - fallbacks handle gracefully so users never see errors
const BubbleSpendMatrix = lazy(() => 
  import('./sections/Spend/BubbleSpendMatrix').catch((error) => {
    // Only log in dev mode - fallback component handles this gracefully
    if (import.meta.env.DEV) {
      logger.debug('BubbleSpendMatrix lazy load failed, using fallback', { error }, 'App');
    }
    // Return a fallback component that shows an error message
    return { default: () => <SectionErrorFallback section="bubble-matrix" /> };
  })
);
const AdminFeeTracker = lazy(() => 
  import('./sections/Spend/AdminFeeTracker').catch((error) => {
    // Only log in dev mode - fallback component handles this gracefully
    if (import.meta.env.DEV) {
      logger.debug('AdminFeeTracker lazy load failed, using fallback', { error }, 'App');
    }
    return { default: () => <SectionErrorFallback section="admin-fee-tracker" /> };
  })
);
const SpendInsightsCharts = lazy(() => 
  import('./components/shared/ui/charts/SpendInsightsCharts').catch((error) => {
    // Only log in dev mode - fallback component handles this gracefully
    if (import.meta.env.DEV) {
      logger.debug('SpendInsightsCharts lazy load failed, using fallback', { error }, 'App');
    }
    return { default: () => <SectionErrorFallback section="spend-insights" /> };
  })
);

// Single source of truth for view modes (prevents string mismatches)
export const SPEND_VIEW_MODES = {
  SIDE_BY_SIDE: 'side-by-side',
  COMBINED: 'combined',
  BUBBLE_AI: 'bubble-ai',
  BUBBLE_MOBILE: 'bubble-mobile',
} as const;

export type SpendViewMode = typeof SPEND_VIEW_MODES[keyof typeof SPEND_VIEW_MODES];

const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span className="ml-3 theme-text-muted">Loading...</span>
  </div>
);

const onRenderCallback = () => {
  // React DevTools automatically captures profiler data
};

type TabType = 'updates' | 'notes' | 'spend' | 'promo-codes';

/**
 * Wraps AppShell with SnapshotScope provider
 * Ensures data is scoped to current workspace and week selection
 */
const SnapshotScopeWrapper: React.FC = () => {
  const { workspaceId } = useUser();
  const { selectedWeek } = useWeekCalculation();

  return (
    <SnapshotScopeProvider workspaceId={workspaceId} periodKey={selectedWeek}>
      <NotesFlushProvider>
        <AppShell key={`${workspaceId ?? 'pending'}:${selectedWeek}`} />
      </NotesFlushProvider>
    </SnapshotScopeProvider>
  );
};

/**
 * Root application component
 * Sets up providers, error boundaries, and performance monitoring
 */
export default function App() {
  React.useEffect(() => {
    initRUM();
    performanceDashboard.initialize();
    initializePerformanceOptimizations();
    applyMobileOptimizations();
    
    return () => {
      performanceDashboard.destroy();
    };
  }, []);

  React.useEffect(() => {
    if (import.meta.env.DEV && 'PerformanceObserver' in window) {
      let lcpObserver: PerformanceObserver | null = null;
      let imgObserver: PerformanceObserver | null = null;
      
      try {
        lcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const el = entry.element as Element | null;
            const size = (entry as unknown as Record<string, unknown>)?.['size'] as number | undefined;
            logger.debug('LCP Inspector', {
              time: Math.round(entry.startTime),
              size,
              tag: el?.tagName,
              id: el instanceof HTMLElement ? el.id : undefined,
              class: el instanceof HTMLElement ? el.className : undefined,
              text: el?.textContent?.slice(0, 80),
              url: entry.url || (el as HTMLImageElement)?.src || 'N/A'
            });
          }
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true } as PerformanceObserverInit);

        imgObserver = new PerformanceObserver((list) => {
          for (const e of list.getEntries() as PerformanceResourceTiming[]) {
            if (e.initiatorType === 'img' || e.initiatorType === 'image') {
              logger.debug('IMG Resource', {
                url: e.name,
                start: Math.round(e.startTime),
                dur: Math.round(e.duration),
                size: 'transferSize' in e ? (e as PerformanceResourceTiming).transferSize : 'N/A'
              });
            }
          }
        });
        imgObserver.observe({ entryTypes: ['resource'] });
      } catch (err) {
        // PerformanceObserver may not be available in all browsers
        if (import.meta.env.DEV) {
          logger.warn('PerformanceObserver initialization failed', { error: err }, 'App');
        }
      }

      // ✅ FIX: Always cleanup, even if initialization failed
      return () => {
        if (lcpObserver) lcpObserver.disconnect();
        if (imgObserver) imgObserver.disconnect();
      };
    }
    return undefined;
  }, []);

  return (
    <ErrorBoundary>
      <UserProvider>
        <SaveStatusProvider>
          <SnapshotFlushProvider>
            <AuthGuard>
              <WeekProvider>
                <SnapshotScopeWrapper />
              </WeekProvider>
            </AuthGuard>
          </SnapshotFlushProvider>
        </SaveStatusProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

const AppShell: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('updates');
  const [transactions, setTransactions] = useState<CampaignTransaction[]>([]);
  const [spendViewMode, setSpendViewMode] = useState<SpendViewMode>(SPEND_VIEW_MODES.COMBINED);
  const { selectedWeek } = useWeekCalculation();
  const { isActuallyAdmin, role, viewerMode } = useUser();
  const notesFlush = useNotesFlushOptional(); // Legacy, kept for compatibility
  const snapshotFlush = useSnapshotFlush(); // ✅ NEW: Universal flush for ALL sections
  const analyticsRole = viewerMode ? 'viewer_mode' : (isActuallyAdmin ? 'admin' : (role ?? 'viewer'));
  const tabAnalyticsRef = React.useRef<{
    tab: TabType;
    periodKey: string;
    role: string;
    startedAt: number;
  } | null>(null);

  useSectionExposureAnalytics({
    activeTab,
    periodKey: selectedWeek,
    role: analyticsRole,
  });

  React.useEffect(() => {
    const now = Date.now();
    const previous = tabAnalyticsRef.current;

    if (previous) {
      const durationMs = now - previous.startedAt;
      if (durationMs >= 1000) {
        trackProductEvent('tab_duration', {
          tab: previous.tab,
          period_key: previous.periodKey,
          role: previous.role,
          duration_ms: durationMs,
        });
      }
    }

    trackProductEvent('tab_viewed', {
      tab: activeTab,
      previous_tab: previous?.tab,
      period_key: selectedWeek,
      role: analyticsRole,
    });

    tabAnalyticsRef.current = {
      tab: activeTab,
      periodKey: selectedWeek,
      role: analyticsRole,
      startedAt: now,
    };
  }, [activeTab, selectedWeek, analyticsRole]);

  React.useEffect(() => {
    return () => {
      const current = tabAnalyticsRef.current;
      if (!current) return;

      const durationMs = Date.now() - current.startedAt;
      if (durationMs >= 1000) {
        trackProductEvent('tab_duration', {
          tab: current.tab,
          period_key: current.periodKey,
          role: current.role,
          duration_ms: durationMs,
        });
      }
    };
  }, []);

  const handleTabChange = useCallback(
    async (tabKey: string) => {
      // ✅ ARCHITECTURAL FIX (per ChatGPT recommendation):
      // "Ensure flushAll() is called synchronously on tab change"
      // This guarantees no dirty state survives context change
      
      console.log('[App] Tab change:', { from: activeTab, to: tabKey });
      
      // Flush ALL sections before navigation (await to ensure completion)
      await snapshotFlush.flushAll();
      
      // Legacy Notes flush (backup, though flushAll() already covered it)
      if (activeTab === 'notes' && tabKey !== 'notes') {
        notesFlush?.getNotesFlush()?.();
      }
      
      setActiveTab(tabKey as TabType);
    },
    [activeTab, notesFlush, snapshotFlush]
  );

  const handleSpendViewModeChange = useCallback((mode: SpendViewMode) => {
    setSpendViewMode(mode);
    trackProductEvent('spend_view_mode_changed', {
      view_mode: mode,
      previous_view_mode: spendViewMode,
      tab: activeTab,
      period_key: selectedWeek,
      role: analyticsRole,
    });
  }, [activeTab, analyticsRole, selectedWeek, spendViewMode]);

  useTabNavigationShortcuts(handleTabChange);
  
  // ✅ ARCHITECTURAL: Flush all sections when week changes
  // Ensures no dirty state survives week context change
  const prevWeekRef = React.useRef(selectedWeek);
  React.useEffect(() => {
    if (prevWeekRef.current !== selectedWeek) {
      console.log('[App] Week change detected:', { from: prevWeekRef.current, to: selectedWeek });
      
      // Flush all sections before week transition
      snapshotFlush.flushAll().then(() => {
        console.log('[App] All sections flushed for week change');
      });
      
      prevWeekRef.current = selectedWeek;
    }
  }, [selectedWeek, snapshotFlush]);

  const renderTabContent = useCallback((): React.ReactElement => {
    switch (activeTab) {
      case 'updates':
        return (
          <ErrorBoundary fallback={<SectionErrorFallback section="updates" />}>
            <Suspense fallback={<LoadingFallback />}>
              <UpdatesSection />
            </Suspense>
          </ErrorBoundary>
        );
      case 'notes':
        return (
          <ErrorBoundary fallback={<SectionErrorFallback section="notes" />}>
            <Suspense fallback={<LoadingFallback />}>
              <NotesSection />
            </Suspense>
          </ErrorBoundary>
        );
      case 'spend':
        return (
          <ErrorBoundary fallback={<SectionErrorFallback section="spend" />}>
            <Suspense fallback={<LoadingFallback />}>
              {/* Section 1: CampaignSpend (top) - First thing user sees */}
              <div className="mb-24">
                <ErrorBoundary fallback={<SectionErrorFallback section="campaign-spend" />}>
                  <CampaignSpend onChange={setTransactions} viewMode={spendViewMode} />
                </ErrorBoundary>
              </div>
              
              {/* Section 2: SpendInsights + BubbleSpendMatrix (grouped together) - Scroll down to see */}
              <div className="mb-24">
                {/* Content container - pixel-identical structure for side-by-side mode */}
                <div className="space-y-12">
                  <Suspense fallback={
                    <div className="flex items-center justify-center theme-muted rounded-lg h-64">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="theme-text-muted text-sm">Loading charts...</p>
                      </div>
                    </div>
                  }>
                    <ErrorBoundary fallback={<SectionErrorFallback section="spend-insights" />}>
                      <SpendInsightsCharts
                        transactions={transactions}
                        className=""
                        viewMode={spendViewMode}
                      />
                    </ErrorBoundary>
                  </Suspense>
                  
                  {/* BubbleSpendMatrix - conditionally render based on viewMode */}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={spendViewMode}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                    >
                      {spendViewMode === SPEND_VIEW_MODES.COMBINED ? (
                        <div className="space-y-8">
                          <ErrorBoundary fallback={<SectionErrorFallback section="bubble-matrix" />}>
                            <BubbleSpendMatrix campaign="Bubble AI" transactions={transactions} />
                          </ErrorBoundary>
                          <ErrorBoundary fallback={<SectionErrorFallback section="bubble-matrix" />}>
                            <BubbleSpendMatrix campaign="Bubble Native Mobile" transactions={transactions} />
                          </ErrorBoundary>
                        </div>
                      ) : spendViewMode === SPEND_VIEW_MODES.BUBBLE_AI ? (
                        <ErrorBoundary fallback={<SectionErrorFallback section="bubble-matrix" />}>
                          <BubbleSpendMatrix campaign="Bubble AI" transactions={transactions} />
                        </ErrorBoundary>
                      ) : spendViewMode === SPEND_VIEW_MODES.BUBBLE_MOBILE ? (
                        <ErrorBoundary fallback={<SectionErrorFallback section="bubble-matrix" />}>
                          <BubbleSpendMatrix campaign="Bubble Native Mobile" transactions={transactions} />
                        </ErrorBoundary>
                      ) : (
                        // Default: side-by-side (pixel-identical to original layout)
                        <div className="flex flex-col lg:flex-row w-full gap-6">
                          <div className="w-full lg:w-1/2">
                            <ErrorBoundary fallback={<SectionErrorFallback section="bubble-matrix" />}>
                              <BubbleSpendMatrix campaign="Bubble AI" transactions={transactions} />
                            </ErrorBoundary>
                          </div>
                          <div className="w-full lg:w-1/2">
                            <ErrorBoundary fallback={<SectionErrorFallback section="bubble-matrix" />}>
                              <BubbleSpendMatrix campaign="Bubble Native Mobile" transactions={transactions} />
                            </ErrorBoundary>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Divider */}
              <div className="mb-40 flex items-center">
                <div className="flex-1 h-px bg-[var(--border-subtle)]"></div>
              </div>

              {/* Section 3: AdminFeeTracker (scroll down more) - Separate section */}
              <div className="mb-40">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={spendViewMode}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                  >
                    {spendViewMode === SPEND_VIEW_MODES.SIDE_BY_SIDE ? (
                      // Side-by-side: two separate fee trackers (AI + Mobile), each with per-campaign threshold
                      <div className="flex flex-col lg:flex-row w-full gap-6">
                        <div className="w-full lg:w-1/2">
                          <ErrorBoundary fallback={<SectionErrorFallback section="admin-fees" />}>
                            <AdminFeeTracker 
                              transactions={transactions.filter(t => String(t.campaign) === 'Bubble AI')}
                              campaign="Bubble AI"
                            />
                          </ErrorBoundary>
                        </div>
                        <div className="w-full lg:w-1/2">
                          <ErrorBoundary fallback={<SectionErrorFallback section="admin-fees" />}>
                            <AdminFeeTracker 
                              transactions={transactions.filter(t => String(t.campaign) === 'Bubble Native Mobile')}
                              campaign="Bubble Native Mobile"
                            />
                          </ErrorBoundary>
                        </div>
                      </div>
                    ) : spendViewMode === SPEND_VIEW_MODES.COMBINED ? (
                      // Combined: one fee tracker with all transactions (global threshold)
                      <ErrorBoundary fallback={<SectionErrorFallback section="admin-fees" />}>
                        <AdminFeeTracker transactions={transactions} />
                      </ErrorBoundary>
                    ) : spendViewMode === SPEND_VIEW_MODES.BUBBLE_AI ? (
                      // Bubble AI only: one fee tracker with AI transactions (per-campaign threshold)
                      <ErrorBoundary fallback={<SectionErrorFallback section="admin-fees" />}>
                        <AdminFeeTracker 
                          transactions={transactions.filter(t => String(t.campaign) === 'Bubble AI')}
                        />
                      </ErrorBoundary>
                    ) : (
                      // Bubble Native Mobile only: one fee tracker with Mobile transactions (per-campaign threshold)
                      <ErrorBoundary fallback={<SectionErrorFallback section="admin-fees" />}>
                        <AdminFeeTracker 
                          transactions={transactions.filter(t => String(t.campaign) === 'Bubble Native Mobile')}
                        />
                      </ErrorBoundary>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </Suspense>
          </ErrorBoundary>
        );
      case 'promo-codes':
        return (
          <ErrorBoundary fallback={<SectionErrorFallback section="promo-codes" />}>
            <Suspense fallback={<LoadingFallback />}>
              <PromoCodesSection />
            </Suspense>
          </ErrorBoundary>
        );
      default:
        return (
          <ErrorBoundary>
            <UpdatesSection />
          </ErrorBoundary>
        );
    }
  }, [activeTab, transactions, spendViewMode]);

  return (
    <div className="min-h-screen theme-muted overflow-x-hidden relative">
      {/* Sticky filter sidebar - only in Spend tab */}
      {activeTab === 'spend' && (
        <SpendFilterSidebar 
          viewMode={spendViewMode} 
          onViewModeChange={handleSpendViewModeChange}
        />
      )}
      <NavBar activeTab={activeTab} onTabChange={handleTabChange} />
      <Profiler id="App" onRender={onRenderCallback}>
        <div data-week={selectedWeek} className="w-full flex flex-col">
          <div className="main-content-container w-full mx-auto">
            <div className="pt-24 sm:pt-28">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </Profiler>
      <FloatingSessionControlWrapper />
      <AuthDiagnostics />
      <ToastContainer position="top-right" maxToasts={5} />
      {import.meta.env.VITE_SMOKE_TEST === 'true' && SmokeTest && (
        <Suspense fallback={null}>
          <SmokeTest />
        </Suspense>
      )}
      {import.meta.env.DEV && <DebugSnapshotsPanel />}
    </div>
  );
};

/**
 * Wrapper for FloatingSessionControl to access user context
 * Separated to avoid prop drilling through multiple components
 */
const FloatingSessionControlWrapper: React.FC = () => {
  const { user, isActuallyAdmin, viewerMode, logout } = useUser();
  
  return (
    <FloatingSessionControl
      user={user}
      isAdmin={isActuallyAdmin}
      viewerMode={viewerMode}
      logout={logout}
    />
  );
};
